const DESKTOP_MEDIA_QUERY = '(min-width: 750px)';
const CLOSE_DELAY_MS = 140;

class HierarchicalMenu extends HTMLElement {
  #closeTimer;
  #documentPointerHandler;
  #documentKeyHandler;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('pointerover', this.#onPointerOver);
    this.addEventListener('pointerout', this.#onPointerOut);
    this.addEventListener('pointerenter', this.#onPointerEnter);
    this.addEventListener('pointerleave', this.#onPointerLeave);
    this.addEventListener('focusin', this.#onFocusIn);
    this.addEventListener('focusout', this.#onFocusOut);

    this.#documentPointerHandler = (event) => {
      if (!this.contains(event.target)) {
        this.closeAll();
      }
    };

    this.#documentKeyHandler = (event) => {
      if (event.key === 'Escape') {
        const activeTrigger = this.querySelector(
          '[data-hierarchical-parent-link][aria-expanded="true"]'
        );

        this.closeAll();
        activeTrigger?.focus();
      }
    };

    document.addEventListener(
      'pointerdown',
      this.#documentPointerHandler
    );

    document.addEventListener(
      'keydown',
      this.#documentKeyHandler
    );
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('pointerover', this.#onPointerOver);
    this.removeEventListener('pointerout', this.#onPointerOut);
    this.removeEventListener('pointerenter', this.#onPointerEnter);
    this.removeEventListener('pointerleave', this.#onPointerLeave);
    this.removeEventListener('focusin', this.#onFocusIn);
    this.removeEventListener('focusout', this.#onFocusOut);

    document.removeEventListener(
      'pointerdown',
      this.#documentPointerHandler
    );

    document.removeEventListener(
      'keydown',
      this.#documentKeyHandler
    );

    window.clearTimeout(this.#closeTimer);
  }

  get isDesktopMode() {
    return (
      this.dataset.mode === 'desktop' &&
      window.matchMedia(DESKTOP_MEDIA_QUERY).matches
    );
  }

  get headerComponent() {
    return this.closest('header-component');
  }

  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const toggle = event.target.closest(
      '[data-hierarchical-toggle]'
    );

    if (toggle && this.contains(toggle)) {
      event.preventDefault();
      event.stopPropagation();

      const item = toggle.closest(
        '[data-hierarchical-item]'
      );

      if (item) {
        this.toggleItem(item);
      }

      return;
    }

    const parentLink = event.target.closest(
      '[data-hierarchical-parent-link]'
    );

    if (!parentLink || !this.contains(parentLink)) {
      return;
    }

    const item = parentLink.closest(
      '[data-hierarchical-item]'
    );

    if (!item) return;

    /*
     * First click or tap opens the submenu.
     * A second click on the already-open parent follows its URL.
     * The adjacent arrow always toggles the submenu.
     */
    if (item.dataset.open !== 'true') {
      event.preventDefault();
      this.openItem(item);
    }
  };

  #onPointerEnter = (event) => {
    if (
      !this.isDesktopMode ||
      event.pointerType === 'touch'
    ) {
      return;
    }

    window.clearTimeout(this.#closeTimer);
  };

  #onPointerLeave = (event) => {
    if (
      !this.isDesktopMode ||
      event.pointerType === 'touch'
    ) {
      return;
    }

    window.clearTimeout(this.#closeTimer);

    this.#closeTimer = window.setTimeout(() => {
      /*
       * Close everything once the pointer has completely
       * left the main menu and all submenu flyouts.
       */
      if (!this.matches(':hover')) {
        this.closeAll();
      }
    }, CLOSE_DELAY_MS);
  };

  #onPointerOver = (event) => {
    if (
      !this.isDesktopMode ||
      event.pointerType === 'touch' ||
      !(event.target instanceof Element)
    ) {
      return;
    }

    const item = event.target.closest(
      '[data-hierarchical-item]'
    );

    if (!item || !this.contains(item)) {
      return;
    }

    /*
     * Ignore pointer movement between descendants
     * of the same menu item.
     */
    if (
      event.relatedTarget instanceof Node &&
      item.contains(event.relatedTarget)
    ) {
      return;
    }

    window.clearTimeout(this.#closeTimer);
    this.openItem(item);
  };

  #onPointerOut = (event) => {
    if (
      !this.isDesktopMode ||
      event.pointerType === 'touch' ||
      !(event.target instanceof Element)
    ) {
      return;
    }

    const item = event.target.closest(
      '[data-hierarchical-item]'
    );

    if (!item || !this.contains(item)) {
      return;
    }

    /*
     * Do not close when moving from a parent row
     * into its own submenu.
     */
    if (
      event.relatedTarget instanceof Node &&
      item.contains(event.relatedTarget)
    ) {
      return;
    }

    window.clearTimeout(this.#closeTimer);

    this.#closeTimer = window.setTimeout(() => {
      /*
       * Clicked links retain browser focus.
       * Do not let stale focus keep the menu and
       * transparent-header white state permanently open.
       */
      if (!item.matches(':hover')) {
        this.closeItem(item);
      }
    }, CLOSE_DELAY_MS);
  };

  #onFocusIn = (event) => {
    if (
      !this.isDesktopMode ||
      !(event.target instanceof Element)
    ) {
      return;
    }

    const item = event.target.closest(
      '[data-hierarchical-item]'
    );

    if (item && this.contains(item)) {
      this.openItem(item);
    }
  };

  #onFocusOut = (event) => {
    if (
      !this.isDesktopMode ||
      !(event.target instanceof Element)
    ) {
      return;
    }

    const item = event.target.closest(
      '[data-hierarchical-item]'
    );

    if (!item || !this.contains(item)) {
      return;
    }

    if (
      event.relatedTarget instanceof Node &&
      item.contains(event.relatedTarget)
    ) {
      return;
    }

    window.setTimeout(() => {
      const activeElement = document.activeElement;

      if (
        !item.contains(activeElement) &&
        !item.matches(':hover')
      ) {
        this.closeItem(item);
      }
    }, 0);
  };

  toggleItem(item) {
    if (item.dataset.open === 'true') {
      this.closeItem(item);
    } else {
      this.openItem(item);
    }
  }

  openItem(item) {
    const submenu = this.getDirectSubmenu(item);

    if (!submenu) return;

    this.closeSiblingItems(item);

    if (this.dataset.mode === 'mobile') {
      submenu.hidden = false;

      /*
       * Force the hidden state to be committed
       * before starting the opening animation.
       */
      submenu.getBoundingClientRect();
    }

    item.dataset.open = 'true';
    this.setItemExpanded(item, true);

    if (this.dataset.mode === 'desktop') {
      this.positionDesktopSubmenu(submenu, item);
      this.setDesktopHeaderOpen(true);
    }
  }

  closeItem(item) {
    const submenu = this.getDirectSubmenu(item);

    if (!submenu) return;

    item.dataset.open = 'false';
    this.setItemExpanded(item, false);

    this.getDirectChildItems(submenu).forEach((child) => {
      this.closeItem(child);
    });

    if (this.dataset.mode === 'mobile') {
      /*
       * Give the closing animation time to finish
       * before hiding the submenu completely.
       */
      window.setTimeout(() => {
        if (item.dataset.open !== 'true') {
          submenu.hidden = true;
        }
      }, 220);
    } else if (
      !this.querySelector(
        '[data-level="1"][data-open="true"]'
      )
    ) {
      this.setDesktopHeaderOpen(false);
    }
  }

  closeAll() {
    this.querySelectorAll(
      '[data-hierarchical-item][data-open="true"]'
    ).forEach((item) => {
      this.closeItem(item);
    });

    if (this.dataset.mode === 'desktop') {
      this.setDesktopHeaderOpen(false);
    }
  }

  closeSiblingItems(item) {
    const parentList = item.parentElement;

    if (!parentList) return;

    Array.from(parentList.children).forEach((sibling) => {
      if (
        sibling !== item &&
        sibling.matches(
          '[data-hierarchical-item][data-open="true"]'
        )
      ) {
        this.closeItem(sibling);
      }
    });
  }

  getDirectSubmenu(item) {
    return (
      Array.from(item.children).find((child) =>
        child.matches?.('[data-hierarchical-submenu]')
      ) ?? null
    );
  }

  getDirectChildItems(submenu) {
    const firstList = Array.from(submenu.children).find(
      (child) => child.matches?.('ul')
    );

    if (!firstList) return [];

    return Array.from(firstList.children).filter((child) =>
      child.matches?.('[data-hierarchical-item]')
    );
  }

  setItemExpanded(item, expanded) {
    const row = Array.from(item.children).find(
      (child) =>
        child.classList?.contains(
          'hierarchical-menu__row'
        ) ||
        child.classList?.contains(
          'hierarchical-drawer__row'
        )
    );

    if (!row) return;

    row
      .querySelectorAll(':scope > [aria-expanded]')
      .forEach((control) => {
        control.setAttribute(
          'aria-expanded',
          String(expanded)
        );
      });
  }

  positionDesktopSubmenu(submenu, item) {
    /*
     * Only third-level flyouts need automatic
     * left or right positioning.
     */
    if (item.dataset.level !== '2') return;

    submenu.dataset.openDirection = 'right';

    requestAnimationFrame(() => {
      const rect = submenu.getBoundingClientRect();

      if (rect.right > window.innerWidth - 12) {
        submenu.dataset.openDirection = 'left';
      }
    });
  }

  setDesktopHeaderOpen(open) {
    const header = this.headerComponent;

    if (!header) return;

    if (!open) {
      header.style.setProperty(
        '--submenu-height',
        '0px'
      );

      header.style.setProperty(
        '--full-open-header-height',
        '0px'
      );

      return;
    }

    /*
     * The original mega menu expanded the underlay
     * beneath a full-width dropdown.
     *
     * This replacement only needs the header row
     * itself to become opaque or white because each
     * flyout has its own background and shadow.
     */
    const visibleHeaderHeight = header.offsetHeight;

    header.style.setProperty(
      '--submenu-height',
      '0px'
    );

    header.style.setProperty(
      '--full-open-header-height',
      `${visibleHeaderHeight}px`
    );
  }
}

if (!customElements.get('hierarchical-menu')) {
  customElements.define(
    'hierarchical-menu',
    HierarchicalMenu
  );
}